/**
 * GraphQL to REST Data Mappers
 *
 * Transform GraphQL response structures to REST-compatible types
 * used by existing components. This enables incremental migration
 * without changing component interfaces.
 *
 * Feature: 063-graphql-frontend
 */

import type { Photo, ImageVariant } from '../types/photo';
import type { Collection, CollectionSummary } from '../types/collection';
import type { PhotoItem2ViewModel } from '../components/PhotoItem2/PhotoItem2.types';
import type {
  GraphQLPhoto,
  GraphQLCollection,
  GraphQLCollectionItem,
  PhotoVariant,
  PaginatedResult,
} from './types';

/**
 * Map GraphQL PhotoVariant to REST ImageVariant
 */
export function mapPhotoVariant(variant: PhotoVariant): ImageVariant {
  return {
    id: variant.id,
    variantType: variant.variantType as ImageVariant['variantType'],
    storageUrl: variant.storageUrl || variant.url || '',
    publicUrl: variant.publicUrl, // Feature 077: Flickr-style redirect URLs
    width: variant.width,
    height: variant.height,
    fileSize: variant.fileSize || 0,
    format: variant.format || '',
  };
}

/**
 * Extract collection count from GraphQL photo response.
 *
 * Handles two response formats during migration:
 * - Paginated: { items: [...], totalCount: N } (from GET_PHOTOS query)
 * - Array: [{ id, name, slug }] (from GET_PHOTO query)
 */
function extractCollectionCount(gqlPhoto: GraphQLPhoto): number {
  if (gqlPhoto.collectionCount != null) return gqlPhoto.collectionCount;
  if (gqlPhoto.collections && 'totalCount' in gqlPhoto.collections) {
    return gqlPhoto.collections.totalCount ?? 0;
  }
  if (Array.isArray(gqlPhoto.collections)) return gqlPhoto.collections.length;
  return 0;
}

/**
 * Extract collections array from GraphQL photo response.
 *
 * Handles two response formats during migration (see extractCollectionCount).
 */
function extractCollections(gqlPhoto: GraphQLPhoto): Array<{ id: string; name: string; slug: string }> {
  if (gqlPhoto.collections && 'items' in gqlPhoto.collections) {
    return gqlPhoto.collections.items ?? [];
  }
  if (Array.isArray(gqlPhoto.collections)) {
    return gqlPhoto.collections;
  }
  return [];
}

/**
 * Map GraphQL Photo to REST Photo type
 */
export function mapGraphQLPhotoToREST(gqlPhoto: GraphQLPhoto): Photo {
  return {
    id: gqlPhoto.id,
    globalId: gqlPhoto.globalId,
    userId: gqlPhoto.userId,
    title: gqlPhoto.title,
    slug: gqlPhoto.slug,
    description: gqlPhoto.description ?? null,
    filename: gqlPhoto.filename,
    fileSize: gqlPhoto.fileSize,
    mimeType: gqlPhoto.mimeType as Photo['mimeType'],
    width: gqlPhoto.width,
    height: gqlPhoto.height,
    metadata: gqlPhoto.metadata ? {
      camera: gqlPhoto.metadata.camera ? {
        make: gqlPhoto.metadata.camera.make,
        model: gqlPhoto.metadata.camera.model,
      } : undefined,
      lens: gqlPhoto.metadata.lens ? {
        make: gqlPhoto.metadata.lens.make,
        model: gqlPhoto.metadata.lens.model,
      } : undefined,
      exposure: {
        iso: gqlPhoto.metadata.iso,
        fNumber: gqlPhoto.metadata.aperture
          ? parseFloat(gqlPhoto.metadata.aperture.replace(/^f\//, '')) || undefined
          : undefined,
        exposureTime: gqlPhoto.metadata.shutterSpeed,
      },
      focus: {
        focalLength: gqlPhoto.metadata.focalLength ? parseInt(gqlPhoto.metadata.focalLength) : undefined,
      },
      capture: {
        dateTimeOriginal: gqlPhoto.metadata.dateTaken,
      },
      location: gqlPhoto.metadata.latitude && gqlPhoto.metadata.longitude ? {
        latitude: gqlPhoto.metadata.latitude,
        longitude: gqlPhoto.metadata.longitude,
      } : undefined,
    } : null,
    variants: gqlPhoto.variants?.map(mapPhotoVariant) || [],
    collectionCount: extractCollectionCount(gqlPhoto),
    collections: extractCollections(gqlPhoto).map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
    })),
    createdAt: gqlPhoto.createdAt,
    updatedAt: gqlPhoto.updatedAt,
  };
}

/**
 * Map GraphQL Collection to REST Collection type
 */
export function mapGraphQLCollectionToREST(gqlCollection: GraphQLCollection): Collection {
  return {
    id: gqlCollection.id,
    globalId: gqlCollection.globalId,
    userId: gqlCollection.userId,
    name: gqlCollection.name || gqlCollection.title,
    slug: gqlCollection.slug,
    description: gqlCollection.description ?? null,
    showExif: gqlCollection.showExif,
    isShared: gqlCollection.isShared,
    photoCount: gqlCollection.photoCount ?? 0,
    heroPhotoId: gqlCollection.heroPhotoId ?? null,
    // Feature 653: Convert GraphQL enum (COLLAGE/FIRST_IMAGE) to internal format (collage/first_image)
    heroStyle: (gqlCollection.heroStyle?.toLowerCase() as 'collage' | 'first_image') ?? 'collage',
    heroPhoto: gqlCollection.heroPhoto ? mapGraphQLPhotoToREST(gqlCollection.heroPhoto) : null,
    sessions: gqlCollection.sessions?.map(s => ({
      id: s.id,
      title: s.title,
      slug: s.slug,
      state: s.state,
      createdAt: s.createdAt,
    })),
    sessionCount: gqlCollection.sessionCount,
    isOrphaned: gqlCollection.isOrphaned,
    createdAt: gqlCollection.createdAt,
    updatedAt: gqlCollection.updatedAt,
  };
}

/**
 * Map GraphQL Collection to CollectionSummary (for list views)
 */
export function mapGraphQLCollectionToSummary(gqlCollection: GraphQLCollection): CollectionSummary {
  return {
    id: gqlCollection.id,
    globalId: gqlCollection.globalId,
    slug: gqlCollection.slug,
    name: gqlCollection.name || gqlCollection.title,
    photoCount: gqlCollection.photoCount ?? 0,
    createdAt: gqlCollection.createdAt,
  };
}

/**
 * Map GraphQL CollectionItem to PhotoItem2ViewModel
 *
 * This is the key mapper for collection display - converts GraphQL
 * collection items into the format expected by PhotoCollection2 and
 * its child components.
 */
export function mapGraphQLCollectionItemToViewModel(
  gqlItem: GraphQLCollectionItem
): PhotoItem2ViewModel {
  if (!gqlItem.photo) {
    throw new Error(`CollectionItem ${gqlItem.id} has no photo data`);
  }

  const photo = mapGraphQLPhotoToREST(gqlItem.photo);

  return {
    photoData: photo,
    critiqueCount: 0, // GraphQL doesn't include critique count on collection items yet
    order: gqlItem.position,
    textContent: gqlItem.caption,
    aspectRatio: photo.width && photo.height
      ? photo.width / photo.height
      : undefined,
  };
}

/**
 * Map paginated GraphQL collection items to PhotoItem2ViewModel array
 */
export function mapCollectionItemsToViewModels(
  items: PaginatedResult<GraphQLCollectionItem>
): PhotoItem2ViewModel[] {
  return items.items.map(mapGraphQLCollectionItemToViewModel);
}

/**
 * Map paginated GraphQL collections to CollectionSummary array
 */
export function mapCollectionsToSummaries(
  collections: PaginatedResult<GraphQLCollection>
): { collections: CollectionSummary[]; totalCount: number } {
  return {
    collections: collections.items.map(mapGraphQLCollectionToSummary),
    totalCount: collections.totalCount,
  };
}

/**
 * Map paginated GraphQL photos to Photo array
 */
export function mapPhotosToREST(
  photos: PaginatedResult<GraphQLPhoto>
): { photos: Photo[]; totalCount: number } {
  return {
    photos: photos.items.map(mapGraphQLPhotoToREST),
    totalCount: photos.totalCount,
  };
}
